import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, Menu, X } from "lucide-react";
import { useAuth } from "../lib/auth";
import { resolveLandingRoute } from "../lib/experienceResolver";
import { LANDING, PUBLIC_NAV } from "../constants/testIds";
import useIsDesktop from "../hooks/useIsDesktop";

const navItems = [
  { label: "About BTC", key: "about", to: "/about", testId: PUBLIC_NAV.navAbout },
  { label: "Get Involved", key: "getInvolved", to: "/get-involved", testId: PUBLIC_NAV.navGetInvolved },
  { label: "Get Support", key: "support", to: "/support", testId: PUBLIC_NAV.navSupport },
  { label: "Stories", key: "stories", to: "/stories", testId: PUBLIC_NAV.navStories },
  { label: "Events", key: "events", to: "/events", testId: PUBLIC_NAV.navEvents },
  { label: "Contact", key: "contact", to: "/contact", testId: PUBLIC_NAV.navContact },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const menuButtonRef = useRef(null);
  const firstItemRef = useRef(null);
  // BTC3 Phase 11 Stage A1 — reactive breakpoint source for the
  // mobile menu dialog's aria-hidden state.
  const isDesktop = useIsDesktop();
  // Collapse the mobile menu once the viewport crosses the lg
  // breakpoint — otherwise the body stays scroll-locked after a
  // resize-up from mobile.
  useEffect(() => {
    if (isDesktop && mobileOpen) setMobileOpen(false);
  }, [isDesktop, mobileOpen]);

  // BTC3 Phase 2 — MY PORTAL no longer hard-codes /portal/dashboard.
  // Ask the Experience Resolver for the authenticated user's canonical
  // landing route.  Per Phase 2 verification §4, a resolver failure
  // must NOT silently drop the user into a workspace — surface the
  // failure via toast and remain on the current page.
  const openMyPortal = async (e) => {
    e.preventDefault();
    try {
      const dest = await resolveLandingRoute();
      navigate(dest);
    } catch (_err) {
      // eslint-disable-next-line no-undef
      if (typeof window !== "undefined" && window.__btc_toast) {
        window.__btc_toast("We could not open your portal. Please retry.");
      }
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Phase 11 — ESC closes the mobile menu; body scroll locks while open;
  // focus moves into the menu on open and returns to the trigger on close.
  useEffect(() => {
    if (!mobileOpen) {
      document.body.classList.remove("btc-scroll-locked");
      return undefined;
    }
    document.body.classList.add("btc-scroll-locked");
    const onKey = (e) => {
      if (e.key === "Escape") {
        setMobileOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    // Defer focus so the anchor is present.
    const t = setTimeout(() => firstItemRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
      document.body.classList.remove("btc-scroll-locked");
    };
  }, [mobileOpen]);

  const isAuthed = user && typeof user === "object";

  const isActive = (to) => location.pathname === to;

  return (
    <header
      data-testid={LANDING.header}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 btc-safe-x ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-[0_1px_20px_rgba(11,19,43,0.06)]" : "bg-white"
      }`}
      style={{ height: 64 }}
    >
      {/* Phase 10 F-S-02 · skip-to-content link, hidden until focused. */}
      <a href="#main-content" className="btc-skip-link" data-testid="public-nav-skip-link">
        Skip to main content
      </a>
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 h-full flex items-center gap-6">
        <Link
          to="/"
          data-testid={PUBLIC_NAV.navHome}
          className="text-[13px] font-bold tracking-[0.14em] text-[#0B132B] uppercase hover:text-[#1A5FAB] transition-colors"
        >
          BTC
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-7 ml-auto" aria-label="Primary">
          {navItems.map((item) => {
            const active = isActive(item.to);
            return (
              <Link
                key={item.key}
                to={item.to}
                data-testid={item.testId}
                aria-current={active ? "page" : undefined}
                className={`text-[12px] font-semibold tracking-[0.04em] uppercase transition-colors pb-1 ${
                  active
                    ? "text-[#1A5FAB] btc-nav-link-active-underline"
                    : "text-[#4B5563] hover:text-[#0B132B]"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2.5 ml-auto lg:ml-2">
          {/* BTC3 Phase 6 D6 — Search was a dead header control with
              no backend search index.  Disabled with honest copy to
              avoid implying a working search.  OrbSearch inside
              Northstar remains the only live search surface. */}
          <button
            data-testid={LANDING.searchButton}
            type="button"
            disabled
            aria-disabled="true"
            title="Site-wide search is coming soon."
            className="hidden md:inline-flex w-9 h-9 items-center justify-center rounded-full text-[#9CA3AF] cursor-not-allowed opacity-60"
            aria-label="Search (coming soon)"
          >
            <Search className="w-4 h-4" strokeWidth={2} />
          </button>
          {isAuthed ? (
            <Link
              to="/portal/dashboard"
              onClick={openMyPortal}
              data-testid={LANDING.loginButton}
              className="hidden sm:inline-flex items-center px-4 py-2 rounded-full border-2 border-[#1A5FAB] text-[#1A5FAB] font-semibold text-xs hover:bg-[#1A5FAB] hover:text-white transition-all"
            >
              MY PORTAL
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                data-testid={LANDING.loginButton}
                className="hidden md:inline-flex items-center px-4 py-2 rounded-full text-[#0B132B] font-semibold text-xs hover:text-[#1A5FAB] transition-all"
              >
                LOG IN
              </Link>
              <Link
                to="/register"
                data-testid="public-nav-join-btc"
                className="hidden sm:inline-flex items-center px-4 py-2 rounded-full border-2 border-[#1A5FAB] text-[#1A5FAB] font-semibold text-xs hover:bg-[#1A5FAB] hover:text-white transition-all"
              >
                JOIN BTC
              </Link>
            </>
          )}
          <Link
            to="/donate"
            data-testid={LANDING.donateButton}
            className="inline-flex items-center px-4 py-2 rounded-full bg-[#23803F] text-white font-semibold text-xs hover:bg-[#1A6632] transition-all shadow-[0_4px_14px_rgba(35,128,63,0.3)]"
          >
            DONATE
          </Link>
          <button
            ref={menuButtonRef}
            className="lg:hidden w-11 h-11 inline-flex items-center justify-center text-[#0B132B]"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            aria-controls="public-mobile-menu"
            data-testid="public-nav-mobile-toggle"
            onClick={() => setMobileOpen((s) => !s)}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          id="public-mobile-menu"
          role="dialog"
          aria-modal="true"
          aria-label="Site navigation"
          data-testid="public-nav-mobile-menu"
          className="lg:hidden bg-white border-t border-gray-100 shadow-xl btc-safe-bottom"
        >
          <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 py-4 flex flex-col gap-1">
            {navItems.map((item, idx) => {
              const active = isActive(item.to);
              return (
                <Link
                  key={item.key}
                  to={item.to}
                  ref={idx === 0 ? firstItemRef : null}
                  data-testid={`${item.testId}-mobile`}
                  aria-current={active ? "page" : undefined}
                  className={`py-3 text-[14px] font-semibold uppercase border-b border-gray-100 ${
                    active ? "text-[#1A5FAB]" : "text-[#0B132B]"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
            <div className="flex gap-3 pt-3">
              {isAuthed ? (
                <Link
                  to="/portal/dashboard"
                  onClick={openMyPortal}
                  className="flex-1 text-center btc-btn-outline"
                >
                  MY PORTAL
                </Link>
              ) : (
                <>
                  <Link to="/login" className="flex-1 text-center btc-btn-outline">
                    LOG IN
                  </Link>
                  <Link
                    to="/register"
                    data-testid="public-nav-join-btc-mobile"
                    className="flex-1 text-center btc-btn-primary justify-center"
                  >
                    JOIN BTC
                  </Link>
                </>
              )}
              <Link to="/donate" className="flex-1 text-center btc-btn-primary justify-center">
                DONATE
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
